import { ReactNode, useMemo } from 'react';

import { ValidationErrors } from 'final-form';

import { Cloud } from '@cast/shared-types';
import { ClusterEditParams } from '@cast/types';
import { makeValidateSync } from '@cast/utils';

import { mapClusterToCloud } from 'utils/csp';
import {
  AWSARNCredentialsSchema,
  AzureCredentialsSchema,
  GCPCredentialsSchema,
} from 'utils/validators';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { RffJsonEditor, RffInput, RffTextArea } from '@cast/shared-rff-ui';

export type ManualCredentialsInputFormValue = {
  credentials?: { json?: string };
  eks?: { assumeRoleArn?: string };
};

type UseManualCredentialsInput = {
  validate?: (value: ManualCredentialsInputFormValue) => ValidationErrors;
  getValue: (value: ManualCredentialsInputFormValue) => ClusterEditParams;
  input: ReactNode;
  initialValue: ManualCredentialsInputFormValue;
};

export const useManualCredentialsInput = (): UseManualCredentialsInput => {
  const { cluster } = useCluster();
  const cloud = mapClusterToCloud(cluster);
  return useMemo(() => {
    const getValue = ({
      credentials,
      eks,
    }: ManualCredentialsInputFormValue): ClusterEditParams => {
      if (eks) {
        return {
          eks,
        };
      }

      return {
        credentials: credentials?.json ? credentials.json : '',
      };
    };

    if (cloud === Cloud.aws) {
      return {
        validate: makeValidateSync<ManualCredentialsInputFormValue>(
          AWSARNCredentialsSchema
        ),
        input: (
          <RffInput
            id="aws-role-arn"
            size="medium"
            name="eks.assumeRoleArn"
            placeholder="Role ARN"
            required
            inputProps={{
              'aria-label': 'credentials aws role arn',
            }}
            testId="manual-input"
          />
        ),
        initialValue: { eks: { assumeRoleArn: '' } },
        getValue,
      };
    }
    if (cloud === Cloud.gcp) {
      return {
        validate:
          makeValidateSync<ManualCredentialsInputFormValue>(
            GCPCredentialsSchema
          ),
        input: (
          <RffJsonEditor
            name="credentials.json"
            rows={5}
            placeholder="Service Account JSON"
          />
        ),
        initialValue: { credentials: { json: '' } },
        getValue,
      };
    }
    if (cloud === Cloud.azure) {
      return {
        validate: makeValidateSync<ManualCredentialsInputFormValue>(
          AzureCredentialsSchema
        ),
        input: (
          <RffTextArea
            autoComplete="off"
            id="credentials-azure"
            name="credentials.json"
            inputProps={{ 'aria-label': 'credentials azure json' }}
            rows={5}
            required
            fullWidth
            placeholder="Credentials JSON"
          />
        ),
        initialValue: { credentials: { json: '' } },
        getValue,
      };
    }
    return {
      input: null,
      initialValue: {},
      getValue,
    };
  }, [cloud]);
};
